/**
 * AuraPill - Give aura to a mention, hashtag, or location. Renders as an aura orb.
 * Tapping the orb opens the shared `AuraCategoriesModal` (multi-select with
 * counts); on radiate, posts each category's points to the aura API and
 * triggers a soft pulse on the orb.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import toast from 'react-hot-toast';
import { selectUser } from '../../store/auth.reducer';
import { decrementRemaining } from '../../store/aura.reducer';
import { createAura } from '../../helpers/auraHelpers';
import AuraCategoriesModal from '../Aura/AuraCategoriesModal';

function AuraPill({ targetId, targetType, postId, commentId, auraCount = 0, onAuraGiven }) {
  const dispatch = useDispatch();
  const currentUser = useSelector(selectUser);
  const [count, setCount] = useState(auraCount);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pulsing, setPulsing] = useState(false);
  const pulseTimeoutRef = useRef(null);

  const isOwnTarget = targetType === 'user' && currentUser?.user_id === targetId;
  const showCountNumber = targetType === 'user' ? true : count > 0;

  useEffect(() => {
    setCount(auraCount);
  }, [auraCount]);

  useEffect(() => {
    return () => {
      if (pulseTimeoutRef.current) clearTimeout(pulseTimeoutRef.current);
    };
  }, []);

  const closeModal = () => setShowModal(false);

  const handleRadiate = async ({ entries, totalSelected }) => {
    if (isOwnTarget) {
      toast.error('You cannot send aura to your own @mention from here.');
      return;
    }
    if (loading) return;

    const payload = { targetId, targetType, postId, commentId };
    setLoading(true);
    try {
      for (const { categoryId, points } of entries) {
        await createAura({ ...payload, categoryId, points });
      }
      toast.success(totalSelected === 1 ? 'Aura sent!' : 'Aura radiated!');
      closeModal();
      setCount((c) => c + totalSelected);
      dispatch(decrementRemaining(totalSelected));
      onAuraGiven?.(totalSelected);
      setPulsing(true);
      if (pulseTimeoutRef.current) clearTimeout(pulseTimeoutRef.current);
      pulseTimeoutRef.current = setTimeout(() => {
        setPulsing(false);
        pulseTimeoutRef.current = null;
      }, 2000);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const openModal = () => {
    if (isOwnTarget) {
      toast(
        'Aura on your own @mention is from other people and postcard deliveries. Give aura using someone else’s pill.',
        { duration: 4200 }
      );
      return;
    }
    setShowModal(true);
  };

  const pillTitle =
    isOwnTarget && targetType === 'user'
      ? 'Aura on your @mention (tap for a tip)'
      : 'Give aura';

  return (
    <span className="relative inline-flex">
      <button
        type="button"
        onClick={openModal}
        className={`inline-flex items-center justify-center gap-1 w-7 h-7 rounded-full text-xs bg-aura/25 text-aura hover:bg-aura/40 border border-aura/50 shadow-glow transition-colors ${pulsing ? 'animate-pulseSoft' : ''}`}
        title={pillTitle}
      >
        <span aria-hidden="true">✦</span>
        {showCountNumber && <span className="font-medium">{count}</span>}
      </button>

      {!isOwnTarget && (
        <AuraCategoriesModal
          open={showModal}
          onClose={closeModal}
          onRadiate={handleRadiate}
          loading={loading}
        />
      )}
    </span>
  );
}

export default AuraPill;
